import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Checkbox, Empty, InputNumber, Space, Spin, Table, Tag, Tooltip, notification } from 'antd';
import { InfoCircleOutlined, UndoOutlined } from '@ant-design/icons';
import lib from '../../../assets/utils/lib';

/**
 * Organisation Manage Plans → Bands and Pricing.
 *
 * Lists every band and sub-band the plan has in the main Plan section, with
 * its base price. Tick the ones this organisation gets; each starts at the
 * base price, which can be changed to the organisation's agreed price. Saving
 * stores the selection and prices for this organisation only — base prices
 * and other organisations never change. Unticking removes a sub-band.
 *
 * mode="add": the plan is not on the organisation yet; saving adds it.
 * mode="manage": the plan is already assigned; add, remove or re-price.
 */

const GROUPS = [
  { key: 'individual', label: 'Individual' },
  { key: 'family', label: 'Family' },
  { key: 'dependent', label: 'Dependents' },
];

const naira = (v) => (v == null || v === '' || Number.isNaN(Number(v))
  ? '—'
  : `₦${Number(v).toLocaleString('en-NG', { maximumFractionDigits: 2 })}`);

const ruleText = (mode, percent) => {
  if (mode === 'same_as_individual') return 'Same as Individual';
  if (mode === 'percent_of_individual') return `${Number(percent)}% of Individual`;
  return null;
};

/** One editable row per band: selected, and the organisation's price (null = base). */
const toDraft = (bands) => {
  const draft = {};
  (bands || []).forEach((b) => {
    draft[b.band_id] = {
      selected: Boolean(b.assigned),
      // A band that follows the Individual price (dependent rule) has no fixed price.
      price: b.assigned ? (b.org_price ?? null) : null,
      followsRule: Boolean(b.assigned && b.org_price == null && ruleText(b.org_pricing_mode, b.org_pricing_percent)),
    };
  });
  return draft;
};

export default function OrgPlanBandSetup({ socialNo, orgId, planId, planName, mode = 'manage', onSaved, onCancel }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [setup, setSetup] = useState(null);
  const [error, setError] = useState(null);
  const [draft, setDraft] = useState({});

  const load = useCallback(async () => {
    if (!planId || (!socialNo && !orgId)) return;
    setLoading(true);
    setError(null);
    const res = await lib.getOrgPlanSetup({ planId, socialNo, orgId });
    const body = res?.data ?? res;
    if (res?.error || body?.error || !body?.data) {
      setError(res?.message || body?.message || 'Could not load the bands for this plan.');
      setSetup(null);
    } else {
      setSetup(body.data);
      setDraft(toDraft(body.data.bands));
    }
    setLoading(false);
  }, [planId, socialNo, orgId]);

  useEffect(() => {
    load();
  }, [load]);

  const bands = useMemo(() => setup?.bands || [], [setup]);
  const selectedCount = Object.values(draft).filter((d) => d.selected).length;
  const dirty = useMemo(() => {
    const original = toDraft(bands);
    return JSON.stringify(original) !== JSON.stringify(draft);
  }, [bands, draft]);

  const update = (bandId, patch) => setDraft((d) => ({ ...d, [bandId]: { ...d[bandId], ...patch } }));

  const save = async () => {
    if (selectedCount === 0) {
      notification.warning({ message: 'Select at least one band or sub-band', placement: 'topLeft' });
      return;
    }
    setSaving(true);
    const payload = bands
      .filter((b) => draft[b.band_id]?.selected)
      .map((b) => ({ band_id: b.band_id, price: draft[b.band_id].price ?? null }));
    const res = await lib.saveOrgPlanSetup({ planId, socialNo, orgId, bands: payload });
    const body = res?.data ?? res;
    setSaving(false);
    if (res?.error || body?.error) {
      notification.error({
        message: 'Could not save',
        description: res?.message || body?.message || 'Please try again.',
        placement: 'topLeft',
      });
      return;
    }
    notification.success({
      message: body?.message || 'Bands and prices saved',
      placement: 'topLeft',
    });
    if (body?.data) {
      setSetup(body.data);
      setDraft(toDraft(body.data.bands));
    }
    onSaved?.(body?.data);
  };

  const columns = [
    {
      title: '',
      key: 'pick',
      width: 44,
      render: (_, b) => (
        <Checkbox
          checked={Boolean(draft[b.band_id]?.selected)}
          onChange={(e) => update(b.band_id, e.target.checked
            ? { selected: true }
            : { selected: false, price: null, followsRule: false })}
          aria-label={`Assign ${b.name}`}
        />
      ),
    },
    {
      title: 'Band / sub-band',
      dataIndex: 'name',
      key: 'name',
      render: (name, b) => (
        <span>
          {name}
          {b.min_age != null || b.max_age != null ? (
            <span style={{ color: '#888', fontSize: 12 }}>
              {' '}· {b.min_age ?? 0}–{b.max_age ?? '∞'} yrs
            </span>
          ) : null}
        </span>
      ),
    },
    {
      title: (
        <Tooltip title="The price set for this band in the main Plan section.">
          Base price <InfoCircleOutlined />
        </Tooltip>
      ),
      key: 'base',
      width: 170,
      render: (_, b) => (
        <span>
          {naira(b.base_price)}
          {ruleText(b.base_pricing_mode, b.base_pricing_percent) ? (
            <div style={{ color: '#888', fontSize: 12 }}>{ruleText(b.base_pricing_mode, b.base_pricing_percent)}</div>
          ) : null}
        </span>
      ),
    },
    {
      title: 'This organisation pays',
      key: 'org',
      width: 260,
      render: (_, b) => {
        const d = draft[b.band_id] || {};
        if (!d.selected) return <span style={{ color: '#bbb' }}>Not assigned</span>;
        const rule = ruleText(b.base_pricing_mode, b.base_pricing_percent);
        return (
          <Space size={6}>
            <InputNumber
              min={1}
              style={{ width: 150 }}
              value={d.price}
              placeholder={d.followsRule || (rule && d.price == null) ? rule : String(Math.round(Number(b.base_price) || 0))}
              onChange={(v) => update(b.band_id, { price: v ?? null, followsRule: false })}
              formatter={(v) => (v == null || v === '' ? '' : `₦ ${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ','))}
              parser={(v) => (v ? v.replace(/[₦,\s]/g, '') : '')}
              aria-label={`${b.name} price for this organisation`}
            />
            {d.price != null ? (
              <Tooltip title="Use the base price">
                <Button size="small" icon={<UndoOutlined />} onClick={() => update(b.band_id, { price: null })} />
              </Tooltip>
            ) : (
              <Tag style={{ marginInlineEnd: 0 }}>{rule ? 'Default rule' : 'Base price'}</Tag>
            )}
          </Space>
        );
      },
    },
  ];

  if (loading) {
    return <div style={{ padding: 16, textAlign: 'center' }}><Spin /></div>;
  }
  if (error) {
    return <Alert type="error" showIcon message={error} style={{ margin: '8px 0' }} />;
  }
  if (!bands.length) {
    return (
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description={`${planName || 'This plan'} has no bands in the main Plan section yet. Set up its bands and prices there first.`}
      />
    );
  }

  return (
    <div className="org-plan-band-setup" style={{ marginTop: 8 }}>
      <p style={{ margin: '0 0 8px', color: '#666', fontSize: 13 }}>
        Tick the bands and sub-bands this organisation gets. Each starts at its base price from the main Plan section;
        type a different amount where the organisation has an agreed price. This changes this organisation only.
      </p>
      {GROUPS.map((g) => {
        const rows = bands.filter((b) => b.premium_type === g.key);
        if (!rows.length) return null;
        return (
          <div key={g.key} style={{ marginBottom: 12 }}>
            <div style={{ fontWeight: 600, margin: '4px 0' }}>{g.label}</div>
            <Table
              size="small"
              rowKey="band_id"
              columns={columns}
              dataSource={rows}
              pagination={false}
              scroll={{ x: 640 }}
            />
          </div>
        );
      })}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8 }}>
        <span style={{ color: '#888', fontSize: 12 }}>
          {selectedCount} band{selectedCount === 1 ? '' : 's'} selected
        </span>
        <Space>
          {onCancel ? <Button onClick={onCancel} disabled={saving}>Cancel</Button> : null}
          <Button type="primary" onClick={save} loading={saving} disabled={selectedCount === 0 || (mode === 'manage' && !dirty)}>
            {mode === 'add' ? 'Add plan' : 'Save changes'}
          </Button>
        </Space>
      </div>
    </div>
  );
}
